<template>
  <div class="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
    <h2 class="text-lg leading-6 font-medium text-gray-900 mb-3">
      {{ t("information") }}
    </h2>
    <form @submit.prevent="updateCustomer">
      <dl class="grid grid-cols-1 gap-x-4 gap-y-8 sm:grid-cols-2">
        <ui-input
          :invalid="'name' in form.errors"
          :error="form.errors.name"
          type="text"
          :label="t('name')"
          v-model="form.name"
        ></ui-input>

        <ui-input
          :invalid="'fiscal_name' in form.errors"
          :error="form.errors.fiscal_name"
          type="text"
          :label="t('fiscal_name')"
          v-model="form.fiscal_name"
          :cornerHint="t('optional')"
        ></ui-input>

        <ui-input
          :invalid="'fiscal_reference' in form.errors"
          :error="form.errors.fiscal_reference"
          type="text"
          :label="t('fiscal_reference')"
          v-model="form.fiscal_reference"
          :cornerHint="t('optional')"
        ></ui-input>

        <div class="sm:col-span-2 flex justify-end">
          <ui-button
            :disabled="form.processing"
            class="w-full sm:w-auto flex items-center justify-center"
            type="submit"
          >
            {{ t("save") }}
          </ui-button>
        </div>
      </dl>
    </form>
    <h2 class="text-lg leading-6 font-medium text-gray-900 mt-6 mb-3">
      {{ t("contacts") }}
    </h2>
    <div class="mt-8 max-w-5xl mx-auto pb-12">
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Link
          :data="{
            search: $page.props.filters.search,
            contacts_page: customerContacts.current_page,
          }"
          :href="
            route('customer-contacts.edit', {
              customer_contact: customerContact.id,
            })
          "
          v-for="customerContact in customerContacts.data"
          :key="customerContact.id"
          class="
            relative
            rounded-lg
            border border-gray-300
            bg-white
            px-6
            py-5
            shadow-sm
            flex
            items-center
            space-x-3
            hover:border-gray-400
            focus-within:ring-2
            focus-within:ring-offset-2
            focus-within:ring-light-blue-500
          "
        >
          <div class="flex-shrink-0">
            <ui-avatar
              backgroundStyling="bg-light-blue-200"
              textStyling="text-white"
              sizeStyling="h-10 w-10"
            />
          </div>
          <div class="flex-1 min-w-0">
            <a href="#" class="focus:outline-none">
              <span class="absolute inset-0" aria-hidden="true"></span>
              <p class="text-sm font-medium text-gray-900 truncate">
                {{ customerContact.name }}
                <span class="text-sm text-gray-500 truncate">
                  {{ customerContact.position }}
                </span>
              </p>
              <p class="text-sm text-gray-500 truncate">
                {{ customerContact.email }}
              </p>
              <p class="text-sm text-gray-500 truncate">
                {{ customerContact.phone }}
              </p>
            </a>
          </div>
        </Link>
        <Link
          :data="{
            search: $page.props.filters.search,
            contacts_page: customerContacts.current_page,
          }"
          :href="
            route('customers.customer-contacts.create', {
              customer: customer.data.id,
            })
          "
          class="
            cursor-pointer
            relative
            rounded-lg
            border border-gray-300
            bg-white
            px-6
            py-5
            shadow-sm
            flex
            items-center
            space-x-3
            hover:border-green-500
            focus-within:ring-2
            focus-within:ring-offset-2
            focus-within:ring-green-500
          "
        >
          <div class="flex-shrink-0">
            <PlusCircleIcon class="w-10 h-10 text-green-500" />
          </div>
          <div class="flex-1 min-w-0">
            <div class="focus:outline-none">
              <span class="absolute inset-0" aria-hidden="true"></span>
              <p class="text-sm font-medium text-gray-900">{{ t("add") }}</p>
              <p class="text-sm text-gray-500 truncate">
                {{ t("new_contact") }}
              </p>
            </div>
          </div>
        </Link>
      </div>
      <pagination
        v-show="customerContacts.data.length > 0"
        :data="{ search: $page.props.filters.search }"
        class="py-3 md:px-0"
        :pagination="customerContacts"
      />
    </div>
  </div>
</template>

<script>
import { PlusCircleIcon } from "@heroicons/vue/outline";
import Pagination from "@/Shared/Molecules/Pagination";
import { useI18n } from "vue-i18n";

export default {
  components: {
    Pagination,
    PlusCircleIcon,
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  props: {
    customer: { type: Object, required: true },

    customerContacts: { type: Object, required: true },
  },

  data() {
    return {
      confirmingCustomerDeletion: false,
      form: this.$inertia.form({
        _method: "PUT",
        name: this.customer.data.name,
        fiscal_name: this.customer.data.fiscal_name,
        fiscal_reference: this.customer.data.fiscal_reference,
      }),
    };
  },

  methods: {
    updateCustomer() {
      this.form.put(
        this.route("customers.update", {
          customer: this.customer.data.id,
          search: this.$page.props.filters.search,
          contacts_page: this.customerContacts.current_page,
        }),
        {
          preserveScroll: true,
        }
      );
    },
  },

  watch: {
    customer(customer, oldCustomer) {
      if (customer.data.id != oldCustomer.id) {
        this.form.clearErrors();
        this.form.name = this.customer.data.name;
        this.form.fiscal_name = this.customer.data.fiscal_name;
        this.form.fiscal_reference = this.customer.data.fiscal_reference;
      }
    },
  },
};
</script>

<i18n>
{
    "es" : {
        "add": "Agregar",
        "new_contact": "Nuevo contacto",
        "save": "Guardar",
        "information": "Información",
        "contacts": "Contactos",
        "name": "Nombre del cliente",
        "fiscal_name": "Razón social",
        "fiscal_reference": "RFC",
        "optional": "opcional",
    }
}
</i18n>